import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { finalizeOnboarding } from '../../api/onboarding';
import { useLearner } from '../../hooks/useLearner';
import { useOnboardingGuard } from '../../hooks/useOnboardingGuard';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import CloudBand from '../../components/common/CloudBand';
import WanderWordsLogo from '../../components/common/WanderWordsLogo';
import OnboardingCard from '../../components/common/OnboardingCard';
import OnboardingPageCard from '../../components/common/OnboardingPageCard';
import ToolTipLing from '../../components/common/ToolTipLing';
import { MAIN_CURRICULA, EXTRA_CURRICULA, ALL_CURRICULA } from '../../constants/curricula';
import s from './OnboardingPages.module.css';

const STEP_COUNT = 5;

export default function CurriculumFineTunePage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { learner: learnerFromState, curriculum: passedCurriculum } = location.state || {};
  const { data: activeLearnerFromQuery } = useLearner();
  const learner = learnerFromState || activeLearnerFromQuery;

  useOnboardingGuard(4);

  const [selected, setSelected] = useState(passedCurriculum?.slug || 'starter_500');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Auto-expand if the learner's pre-existing curriculum is in the extra set
  const [showAll, setShowAll] = useState(
    EXTRA_CURRICULA.some(c => c.slug === (passedCurriculum?.slug || 'starter_500'))
  );

  const learnerName = learner?.name || t('childProfile.yourChild');
  const currentStep = 4;

  async function handleContinue() {
    setIsLoading(true);
    setError(null);
    try {
      const childProfileId = learner?.id;

      if (childProfileId) {
        // Atomic finalize (Packet A1): one server-side transaction replaces
        // the prior three-call chain. The server commits curriculum assignment,
        // learner activation, and tier-1 character state init together, or
        // rolls everything back.
        await finalizeOnboarding(childProfileId, selected);
      }

      // FP-LEARNER-STORE-RETIREMENT-01: optimistic patch into the
      // ['learners'] cache so PlacementBandPage reads the active status +
      // bumped onboarding_step on first render. Then invalidate so the
      // server-side curriculum_id / cached_level reconcile in the background.
      if (learner) {
        queryClient.setQueryData(['learners'], (prev) =>
          Array.isArray(prev)
            ? prev.map((l) =>
                l.id === learner.id
                  ? { ...l, status: 'active', onboarding_step: 4 }
                  : l
              )
            : prev
        );
      }
      queryClient.invalidateQueries({ queryKey: ['learners'] });

      // A2: onboarding_complete is derived in GET /users/me from learner state.
      // Fire-and-forget — do NOT await. Awaiting the refetch causes a race:
      // if the ['me'] cache updates (onboarding_complete=true) before
      // navigate() fires, the OnboardingGuard redirects to '/' and the next
      // step never renders.
      queryClient.invalidateQueries({ queryKey: ['me'] });

      const targetChildId = childProfileId || learner?.id;
      navigate('/onboarding/placement-band', {
        replace: true,
        state: { childProfileId: targetChildId },
      });
    } catch (err) {
      // FIX: show error inline — do NOT navigate away on failure
      setError(err.message || t('common.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  }

  // Shared step counter markup
  const stepCounter = (
    <div className={s.stepCounter}>
      {Array.from({ length: STEP_COUNT }, (_, i) => {
        const step = i + 1;
        const isActive = step <= currentStep;
        return (
          <div key={step} className={`${s.stepDot} ${isActive ? s.stepDotActive : ''}`}>
            {step}
          </div>
        );
      })}
    </div>
  );

  // Shared curriculum cards markup. When collapsed: 3 main cards + "show more"
  // button. When expanded: all 7 cards inside a fixed-height scroll container
  // (the original 3 are inside the scroll region, not above it).
  const renderCard = (c) => (
    <OnboardingCard
      key={c.slug}
      title={t(c.nameKey)}
      description={t(c.descKey)}
      selected={selected === c.slug}
      onClick={() => setSelected(c.slug)}
      badge={c.recommended ? t('curriculum.recommended') : undefined}
    />
  );

  const curriculumCards = (
    <div className={s.optionCards} role="radiogroup">
      {!showAll ? (
        <>
          {MAIN_CURRICULA.map(renderCard)}
          <button
            type="button"
            className={s.moreOptionsBtn}
            onClick={() => setShowAll(true)}
          >
            {t('curriculum.moreOptions')}
          </button>
        </>
      ) : (
        <div className={s.curriculumScroll}>
          {ALL_CURRICULA.map(renderCard)}
        </div>
      )}
    </div>
  );

  return (
    <main
      className={s.page}
      style={{ '--cloud-color': '#2AB8A8' }}
      ref={mainRef}
      tabIndex={-1}
    >
      {/* Desktop decoration — cream top band + cloud seam (hidden on mobile by CSS) */}
      <div className={`${s.topBand} ${s.topBandTeal}`} />
      <CloudBand variant="teal" className={s.cloudBandTeal} />
      <WanderWordsLogo className={s.logo} />

      {/* ───────────── MOBILE / TABLET LAYOUT (hidden ≥1200px) ───────────── */}
      <div className={s.mobileOnly}>
        <div className={s.content}>
          <div className={s.contentRow}>
            {/* Left column — teal section */}
            <div className={`${s.leftCol} ${s.leftColTeal}`}>
              {stepCounter}
              <div className={`${s.headerRow} ${s.headerRowTypeA}`}>
                <div className={s.headerColumn}>
                  <h1 className={s.pageTitle}>{t('curriculum.title')}</h1>
                  <p className={s.subtitle}>
                    {t('curriculum.subtitle', { name: learnerName })}
                  </p>
                </div>
                <img
                  src="/assets/ling-onboarding.png"
                  alt="Ling the cat"
                  className={s.mascotImg}
                />
              </div>
            </div>
          </div>

          {/* Cream bottom section — cards + continue button */}
          <div className={s.mobileCreamSection}>
            <div className={s.rightCol}>
              {error && (
                <div className={s.errorBanner} role="alert">{error}</div>
              )}
              {curriculumCards}
              <div className={s.spacer} />
            </div>

            <div className={s.stickyBtnArea}>
              <div className={s.btnRow}>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className={s.cancelBtn}
                >
                  {t('common.back')}
                </button>
                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={isLoading}
                  className={s.continueBtn}
                >
                  {isLoading ? t('curriculum.gettingReady') : t('common.continue')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ───────────── DESKTOP CARD (≥1200px only) ───────────── */}
      <OnboardingPageCard
        stepper={stepCounter}
        leftWidth={392}
        leftContent={
          <>
            <div className={s.headerColumn}>
              <h1 className={s.pageTitle}>{t('curriculum.title')}</h1>
              <p className={s.subtitle}>
                {t('curriculum.subtitle', { name: learnerName })}
              </p>
            </div>
            <ToolTipLing type="curious">
              {t('curriculum.tooltipText')}
            </ToolTipLing>
          </>
        }
        rightContent={
          <>
            {error && (
              <div className={s.errorBanner} role="alert">{error}</div>
            )}
            {curriculumCards}
            <div className={s.btnRow}>
              <button type="button" onClick={() => navigate(-1)} className={s.cancelBtn}>
                {t('common.back')}
              </button>
              <button
                type="button"
                onClick={handleContinue}
                disabled={isLoading}
                className={s.continueBtn}
              >
                {isLoading ? t('curriculum.gettingReady') : t('common.continue')}
              </button>
            </div>
          </>
        }
      />
    </main>
  );
}
